import { useTranslation } from 'react-i18next'
import { useAppStore, usePMTiles } from '~/hooks'
import { GroupedMenuSelect } from '~/components/MenuSelect/index'
import Slider from '../Slider'
import { Interpolated } from '../Interpolated'
import {
  BATHYMETRY_DEPTH_COLORS,
  DIRECTIONAL_ATTRIBUTES,
  hsReturnPeriods,
  PMTileSources,
  VisibleLayers,
  type WaveUnit,
  WAVES_OPTIONS,
  WAVES_OPTIONS_GROUPED,
  WAVE_UNITS,
} from '~/constants'
import { Text } from '../typography'
import { Checkbox } from '../inputs'
import MultiSwitch from '../MultiSwitch'
import MultiSwitchOption from '../MultiSwitch/MultiSwitchOption'
import { colorBreaksForAttributeAndDepths } from '~/util/waves'
import { track } from '~/analytics'

const DEPTHS = [10, 20, 50, 100] as const

const WAVES_VALUE_TO_I18N: Record<string, { label: string; desc: string }> = {
  hs_rp_100: { label: 'waves.hsRp100', desc: 'waves.hsRp100Desc' },
  hs_rp_50: { label: 'waves.hsRp50', desc: 'waves.hsRp50Desc' },
  Hs12: { label: 'waves.hs12', desc: 'waves.hs12Desc' },
  DirMEF: { label: 'waves.meanEnergyFluxDirection', desc: 'waves.meanEnergyFluxDirectionDesc' },
  extremes_energy_flux_averaged_direction: {
    label: 'waves.extremeEnergyFluxDirection',
    desc: 'waves.extremeEnergyFluxDirectionDesc',
  },
  WPsum: { label: 'waves.totalWavePower', desc: 'waves.totalWavePowerDesc' },
  Hsmean_mean_percentage_change: {
    label: 'waves.changeInMeanHs',
    desc: 'waves.changeInMeanHsDesc',
  },
  Hs_q99_mean_percentage_change: {
    label: 'waves.changeInHs99thPctl',
    desc: 'waves.changeInHs99thPctlDesc',
  },
  hs_rp_100_mean_percentage_change: {
    label: 'waves.changeInHsRp100',
    desc: 'waves.changeInHsRp100Desc',
  },
  WPmean_mean_percentage_change: {
    label: 'waves.changeInMeanWavePower',
    desc: 'waves.changeInMeanWavePowerDesc',
  },
  hs_change: { label: 'waves.projectedHsChangeByRp', desc: 'waves.projectedHsChangeByRpDesc' },
}

function getColorBarTitle(
  wavesProperty: string | undefined,
  wavesPropertyToUse: string,
  defaultOption: { value: string } | undefined,
  t: (key: string) => string
): string {
  const effectiveProperty = wavesProperty ?? defaultOption?.value
  if (
    effectiveProperty &&
    DIRECTIONAL_ATTRIBUTES.includes(effectiveProperty as (typeof DIRECTIONAL_ATTRIBUTES)[number])
  ) {
    return t('legend.wavePower')
  }
  const labelKey =
    WAVES_VALUE_TO_I18N[wavesPropertyToUse]?.label ??
    (defaultOption?.value && WAVES_VALUE_TO_I18N[defaultOption.value]?.label) ??
    'legend.waveAttribute'
  return t(labelKey)
}

export function CalWaves() {
  const { t } = useTranslation()
  const { layerInfo } = usePMTiles()
  const {
    wavesProperty,
    setWavesProperty,
    setDepth,
    depths,
    setHsReturnPeriod,
    hsReturnPeriod,
    dynamicWavesProperty,
    hsYear,
    setHsYear,
    toggleLayerVisibility,
    layerVisibility,
    setBasemapType,
  } = useAppStore()
  const wavesLayer = layerInfo?.[PMTileSources.WAVES]
  let wavesPropertyToUse = dynamicWavesProperty || wavesProperty

  // If we are looking at one of the directional attributes then we display
  // arrows whose color and size correlate to WPsum, rather than the selected
  // attribute.
  if (DIRECTIONAL_ATTRIBUTES.includes(wavesPropertyToUse)) {
    wavesPropertyToUse = 'WPsum'
  }

  const selectedAttribute = wavesLayer?.[0]!.attributes.find(
    (attr) => attr.attribute === wavesPropertyToUse
  )
  const unit = wavesPropertyToUse !== 'hs_change' && WAVE_UNITS[wavesPropertyToUse]
  const showBathymetry = layerVisibility[VisibleLayers.BATHYMETRY]
  const bathymetryPairs = BATHYMETRY_DEPTH_COLORS.map(
    ([value, color]) => [value, color] as [number, string]
  )

  const defaultOption = WAVES_OPTIONS.find((opt) => opt.value === wavesProperty) ?? WAVES_OPTIONS[0]
  const defaultValue = defaultOption
    ? {
        ...defaultOption,
        label: t(WAVES_VALUE_TO_I18N[defaultOption.value]?.label ?? defaultOption.value),
        description: t(WAVES_VALUE_TO_I18N[defaultOption.value]?.desc ?? ''),
      }
    : undefined

  const translatedGroups = WAVES_OPTIONS_GROUPED.map((group) => ({
    header: t(
      group.header === 'Historic wave climate'
        ? 'waves.historicWaveClimate'
        : 'waves.waveClimateProjections'
    ),
    options: group.options.map((opt) => ({
      ...opt,
      label: t(WAVES_VALUE_TO_I18N[opt.value]?.label ?? opt.value),
      description: t(WAVES_VALUE_TO_I18N[opt.value]?.desc ?? opt.description),
    })),
  }))

  const colorPairs = colorBreaksForAttributeAndDepths(depths, wavesPropertyToUse, wavesLayer!)

  const handleWaveAttributeChange = (attribute: WaveUnit) => {
    setWavesProperty(attribute)
    track('wave_attribute_selected', { attribute })
  }

  return (
    <div className="space-y-4">
      {translatedGroups.length > 0 && defaultValue && (
        <div className="space-y-1 text-left">
          <Text className="text-neutral-midfi-grey" level={4}>
            {t('legend.pointAttribute')}
          </Text>
          <GroupedMenuSelect
            groups={translatedGroups}
            defaultValue={defaultValue}
            onChange={(option) => handleWaveAttributeChange(option.value)}
          />
          {defaultValue.description && (
            <Text className="italic text-neutral-midfi-grey" level={4}>
              {defaultValue.description}
            </Text>
          )}
        </div>
      )}
      <div className="space-y-2 text-left">
        <Text className="text-neutral-midfi-grey" level={4}>
          {t('legend.depth')}
        </Text>
        <div className="flex flex-col space-y-2">
          {DEPTHS.map((depth) => (
            <Checkbox
              key={depth}
              checked={depths.includes(depth)}
              onChange={() => {
                const enabled = !depths.includes(depth)
                setDepth(depth)
                track('wave_depth_toggled', { depth, enabled })
              }}
            >
              <Text level={4} className="text-neutral-midfi-grey">
                {t('legend.meters', { count: depth })}
              </Text>
            </Checkbox>
          ))}
        </div>
      </div>
      <div className="space-y-2 text-left">
        <Text className="text-neutral-midfi-grey" level={4}>
          {t('legend.context')}
        </Text>
        <div className="flex flex-col space-y-2">
          <Checkbox
            checked={showBathymetry}
            onChange={() => {
              const nextVisible = !showBathymetry
              const nextBasemap = nextVisible ? 'FLAT' : 'SATELLITE'
              toggleLayerVisibility(VisibleLayers.BATHYMETRY)
              setBasemapType(nextBasemap)
              track('layer_visibility_toggled', {
                layer: VisibleLayers.BATHYMETRY,
                visible: nextVisible,
              })
              track('basemap_selected', { basemap: nextBasemap })
            }}
          >
            <Text level={4} className="text-neutral-midfi-grey">
              {t('legend.bathymetry')}
            </Text>
          </Checkbox>
        </div>
      </div>
      {wavesProperty === 'hs_change' && (
        <>
          <div className="flex flex-col items-center gap-1">
            <Text className="self-start text-neutral-midfi-grey" level={4}>
              {t('legend.returnPeriodYears')}
            </Text>
            <Slider
              options={hsReturnPeriods}
              value={hsReturnPeriod}
              onChange={(nextPeriod) => {
                setHsReturnPeriod(nextPeriod)
                track('scenario_updated', { setting: 'hs_return_period', value: nextPeriod })
              }}
              ariaLabel={t('legend.sliderReturnPeriod')}
            />
          </div>
          <div className="space-y-1 text-left">
            <Text className="text-neutral-midfi-grey" level={4}>
              {t('legend.yearComparison')}
            </Text>
            <div className="flex justify-center pt-1">
              <MultiSwitch<'2055' | '2085'>
                onChange={(year) => {
                  setHsYear(year)
                  track('scenario_updated', { setting: 'hs_year', value: year })
                }}
                selection={hsYear}
              >
                <MultiSwitchOption<'2055' | '2085'>
                  value="2055"
                  position="left"
                  title={t('legend.presentVs2055')}
                />
                <MultiSwitchOption<'2055' | '2085'>
                  value="2085"
                  position="right"
                  title={t('legend.presentVs2085')}
                />
              </MultiSwitch>
            </div>
          </div>
        </>
      )}
      {(selectedAttribute && unit) || showBathymetry ? (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {selectedAttribute && unit && (
            <div className="space-y-1 text-left">
              <Text className="text-neutral-midfi-grey" level={4}>
                {getColorBarTitle(wavesProperty, wavesPropertyToUse, defaultOption, t)}
              </Text>
              <Interpolated pairs={colorPairs} unit={unit} />
            </div>
          )}
          {showBathymetry && (
            <div className="space-y-1 text-left">
              <Text className="text-neutral-midfi-grey" level={4}>
                {t('legend.bathymetryDepth')}
              </Text>
              <Interpolated pairs={bathymetryPairs} unit="m" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
